import { Form, Input, InputRef, Row, Select, Spin } from 'antd';
import { AxiosResponse } from 'axios';
import DateMonthInput from 'components/BirthdayInput';
import Modal from 'components/Modal';
import PhoneInput from 'components/PhoneInput';
import moment from 'utils/moment';
import { useRef, useState } from 'react';
import shopActions from 'services/actions/shop';
import apis from 'services/apis';
import { IBodyUpdateCustomerDetail } from 'services/types/apt';
import { IAddNewCustomerResData } from 'services/types/response';
import { useAppDispatch } from 'store/hooks';
import styled from 'styled-components';
import { ICustomerInfoResData, ICustomerUI } from 'types/customer';
import { formatPhone } from 'utils/formatPhone';
import { verifyPinCode } from 'widgets/ModalVerifyAdminPin';

interface IAddCustomerFormValues {
  name: string;
  phoneNumber: string;
  email: string;
  birthday: string;
  gender: string;
}

type Props = {
  onSelect: (data: ICustomerUI) => void;
  id: string;
};

const EditCustomer = ({ onSelect, id }: Props) => {
  const inputRef = useRef<InputRef>(null);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [open, setOpen] = useState(false);
  const [isWalkIn, setIsWalkIn] = useState(false);
  const dispatch = useAppDispatch();

  const fetchInfo = async (_id: string) => {
    setLoading(true);
    try {
      const res: AxiosResponse<{ payload: ICustomerInfoResData }> = await apis.getDetailCustomer(_id);
      const detail = res?.data?.payload;
      if (detail) {
        const body: IAddCustomerFormValues = {
          phoneNumber: formatPhone(detail.phone),
          email: detail.email,
          name: detail.name,
          birthday: moment(detail.birthday).format('MM/DD/YYYY'),
          gender: detail.gender || '',
        };
        setIsWalkIn(false);
        form.setFieldsValue(body);
      } else {
        setIsWalkIn(true);
      }
    } catch (error) {
      setIsWalkIn(true);
    }
    finally {
      setLoading(false);
    }
  };


  const handleFinish = async (_values: any) => {
    const values = _values as IAddCustomerFormValues;
    if (isWalkIn) {
      const newCus: ICustomerUI = {
        id: 're-update-walk-in',
        name: values.name,
        phone: values.phoneNumber,
      };
      onSelect(newCus);
      handleClose();
      return;
    }
    const body: IBodyUpdateCustomerDetail = {
      birthDay: values.birthday,
      email: values.email,
      fullName: values.name,
      gender: values.gender,
      phone: values.phoneNumber?.replace(/[^0-9]/g, '')
    };
    setLoading(true);
    try {
      const res: AxiosResponse<{ payload: IAddNewCustomerResData | null }> = await apis.updateCustomerDetail(id, body);
      const data = res?.data?.payload;

      if (data) {
        const newCus: ICustomerUI = {
          id: data.customerId?.toString(),
          name: data.customerName,
          phone: data.customerPhone,
        };
        dispatch(shopActions.afterUpdateCustomer(newCus));
        onSelect(newCus);
        handleClose();
      }
    } catch (error) { } finally {
      setLoading(false);
    }
  };

  const handleOpen = () => {
    verifyPinCode.current?.forceVerifyAdmin(() => {
      setOpen(true);
      fetchInfo(id);
    });
  };

  const handleClose = () => {
    setOpen(false);
    form.resetFields();
  };

  return (
    <>
      <ButtonChange onClick={handleOpen}><Change /></ButtonChange>
      <Modal
        open={open}
        onCancel={handleClose}
        onOk={() => form.submit()}
        title="EDIT CUSTOMER"
        width={'60vw'}
        height={'340px'}
        disabledOk={loading}
      >
        <Spin spinning={loading}>
          <FormStyled form={form} layout='vertical' size='large' onFinish={handleFinish} validateTrigger={['onSubmit']}>
            <Row className='group'>
              <Form.Item className='col-item' name={'name'} label={'CUSTOMER NAME'} rules={[{ required: true }]}
              >
                <Input ref={inputRef} placeholder='Please enter' />
              </Form.Item>
            </Row>
            <Row className='group'>
              <Form.Item className='col-item' name={'phoneNumber'} label={'PHONE NUMBER'} rules={[{ required: true }]}
              >
                <PhoneInput />
              </Form.Item>
              <Form.Item className='col-item' name={'email'} label={'EMAIL'} rules={[{ type: 'email' }]}>
                <Input placeholder='Please enter' />
              </Form.Item>
            </Row>
            <Row className='group'>
              <Form.Item className='col-item' name={'birthday'} label={'BIRTHDAY'}>
                <DateMonthInput />
              </Form.Item>
              <Form.Item className='col-item' name={'gender'} label={'GENDER'}>
                <Select
                  defaultValue="none"
                  options={[
                    { value: 'male', label: 'Male' },
                    { value: 'female', label: 'Female' },
                    { value: 'none', label: 'None' },
                  ]}
                />
              </Form.Item>
            </Row>
          </FormStyled>
        </Spin>
      </Modal>
    </>
  );
};

export default EditCustomer;

const FormStyled = styled(Form)`
  padding: 1rem 1.5rem;
  .group {
    gap: 1.5rem;
  }
  .col-item {
    flex:1;
  }
  input.ant-input {
    line-height: 2;
    &::placeholder {
      color:#dadada;
    }
  }
  .ant-select-single.ant-select-lg {
    height: 46px;
  }
  &.ant-form-large .ant-form-item .ant-form-item-label>label {
    height: 24px;
  }
`;

const ButtonChange = styled.button`
  display: flex;
  height: 30px;
  width: 40px;
  margin-left: 4px;
  align-items: center;
  justify-content: center;
  &:hover {
    background: #e9e8e8;
  }
`;

const Change = () => {
  return (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path fillRule="evenodd" clipRule="evenodd" d="M5.33301 6.66691C4.97939 6.66691 4.64025 6.80739 4.3902 7.05743C4.14015 7.30748 3.99967 7.64662 3.99967 8.00024V26.6669C3.99967 27.0205 4.14015 27.3597 4.3902 27.6097C4.64025 27.8598 4.97939 28.0002 5.33301 28.0002H23.9997C24.3533 28.0002 24.6924 27.8598 24.9425 27.6097C25.1925 27.3597 25.333 27.0205 25.333 26.6669V21.3336C25.333 20.5972 25.93 20.0002 26.6663 20.0002C27.4027 20.0002 27.9997 20.5972 27.9997 21.3336V26.6669C27.9997 27.7278 27.5782 28.7452 26.8281 29.4953C26.078 30.2455 25.0605 30.6669 23.9997 30.6669H5.33301C4.27214 30.6669 3.25473 30.2455 2.50458 29.4953C1.75444 28.7452 1.33301 27.7278 1.33301 26.6669V8.00024C1.33301 6.93938 1.75443 5.92196 2.50458 5.17182C3.25473 4.42167 4.27214 4.00024 5.33301 4.00024H10.6663C11.4027 4.00024 11.9997 4.5972 11.9997 5.33358C11.9997 6.06996 11.4027 6.66691 10.6663 6.66691H5.33301Z" fill="#148CF1" />
      <path fillRule="evenodd" clipRule="evenodd" d="M22.7902 1.72402C23.3109 1.20332 24.1551 1.20332 24.6758 1.72402L30.2758 7.32402C30.7946 7.84279 30.7968 8.68321 30.2807 9.20469L17.6141 22.0047C17.378 22.2432 17.0608 22.384 16.7255 22.3988L10.7255 22.6655C10.3466 22.6824 9.97839 22.537 9.71315 22.2658C9.44791 21.9946 9.31073 21.6233 9.33596 21.2448L9.73596 15.2448C9.75745 14.9226 9.89517 14.6191 10.1235 14.3907L22.7902 1.72402ZM23.733 4.55245L12.3634 15.9221L12.0958 19.9353L16.0857 19.758L27.4523 8.27175L23.733 4.55245Z" fill="#148CF1" />
    </svg>
  );
};
